// Local
import { IAlertLock } from "../../../../modules/alerts/alert-group"
import { IAlertFilter } from "../../../../modules/alerts/filter"
import { IAlert } from "../../../models/alert.model"
import { IAlertState, IAlertWindow } from "../alert.states"
import { getValidAlerts } from "./alert.helpers"
import { alertMatchesCurrentFilter, sortAlertsByFilter } from "./filter.helpers"


// region CONSTANTS
export interface IWindowSettings {
    interval: number
    size: number
}

export const DEFAULT_EMPTY_WINDOW: Partial<IAlertWindow> = {
    alerts: [],
    fullAlerts: [],
    currentAlert: 0,
    currentAlertId: undefined,
    selected: [],
    windows: [],
    alertsToBeDismissed: [],
    currentWindowIndex: 0,
    previousWindowIndex: 0,
    requestingNextLock: false
};

function _getValidNeighbours(alert: IAlert, alerts: IAlert[], locks: Record<string, IAlertLock>, window: IAlertWindow, filter: IAlertFilter): IAlert[] {
    const index = alerts.findIndex(e => e.id === alert.id);
    if (index === -1) {
        return [];
    }

    return alerts.slice(index + 1).filter(e => {
        const isSameSite: boolean = e.site_id === alert.site_id;
        const isPotentialNeighbourLocked: boolean = locks[e.id] != null;
        const alertWithinGroupInterval: boolean = Math.abs(new Date(alert.creation_timestamp).getTime() - new Date(e.creation_timestamp).getTime()) < (window.interval * 1e3)
        const alertMatchesFilter: boolean = alertMatchesCurrentFilter(e, filter);
        return isSameSite && !isPotentialNeighbourLocked && alertMatchesFilter && (window.interval == 0 || alertWithinGroupInterval);
    });
}

export function getInitialWindow(state: IAlertState, alerts: IAlert[] = [], isPaused: boolean | null = null): IAlertWindow {
    let defaultEmptyWindow: IAlertWindow = {
        ...state.window,
        ...DEFAULT_EMPTY_WINDOW
    };

    if (isPaused == null) {
      isPaused = state.paused.paused
    }

    if (isPaused) {
        return {
            ...defaultEmptyWindow,
            windows: state.window.windows
        }
    };

    if (alerts.length === 0) {
      alerts = getValidAlerts(state)
    }
    else {
      alerts = getValidAlerts(state, alerts)
    }
    alerts = sortAlertsByFilter(state.filter.ordering, alerts)
    const windowAlertIDs: string[] | null = state.window.alerts;
    if (windowAlertIDs != null) {
      alerts = alerts.filter(alert => !state.window.alerts.includes(alert.id))
    }

    // NOTE: below is so we still have the alert ids for dismissing
    if (isPaused && state.pauseAfterAlertAction) {
        defaultEmptyWindow = {
            ...defaultEmptyWindow,
            alerts: state.window.alerts.filter((alert) => state.window.selected.includes(alert)),
            selected: state.window.selected
        }
    }

    if (alerts.length === 0 || isPaused) {
      return defaultEmptyWindow;
    }

    const firstAlert: IAlert | undefined = alerts.find(alert => state.locks[alert.id] == null);
    if (firstAlert == null) {
      return defaultEmptyWindow;
    }

    const neighbours: IAlert[] = _getValidNeighbours(firstAlert, alerts, state.locks, state.window, state.filter).slice(0, state.window.size - 1);
    const alertList: IAlert[] = sortAlertsByFilter(state.filter.ordering, [firstAlert, ...neighbours]);
    const windows = buildAlertWindows(state.alerts.items, state.filter, state.window.size, state.window.interval);
    if (alertList.length < 1) {
      return defaultEmptyWindow;
    }

    return {
        ...state.window,
        windows: windows,
        alerts: [],
        fullAlerts: [],
        selected: [],
        currentAlert: 0,
        currentAlertId: undefined,
        currentWindowIndex: 0
    };
}

export function getWindowAlertIndex(state: IAlertState, index: number): { currentAlert: number, currentAlertId: string | undefined } {
    if (index >= state.window.fullAlerts.length || index < 0) {
        const validAlerts: IAlert[] = getValidAlerts(state);
        const newWindow: IAlertWindow = getInitialWindow(state, validAlerts);
        return {
            currentAlert: newWindow.currentAlert,
            currentAlertId: newWindow.currentAlertId
        }
    }

    return {
        currentAlert: index,
        currentAlertId: state.window.fullAlerts[index].id
    }
}

export function updateWindowSettings(state: IAlertState, interval: number, size: number): IAlertWindow {
    const newWindow = {
        ...state.window,
        interval: interval,
        size: size
    }

    const validAlerts: IAlert[] = getValidAlerts(state);
    const validWindowAlertIDs: string[] = state.window.fullAlerts.map(e => e.id);
    const index = validAlerts.findIndex(e => e.id === validWindowAlertIDs[validWindowAlertIDs.length - 1])
    if (index === -1) {
        return newWindow
    }
    const possibleAlerts = validAlerts.slice(index)

    return getInitialWindow({
        ...state,
        window: newWindow
    }, possibleAlerts)
}

export function moveAlertWindow(state: IAlertState, targetAlert: IAlert): IAlertWindow {
    const validAlerts: IAlert[] = getValidAlerts(state);
    const alertIndex = validAlerts.findIndex(alert => alert.id === targetAlert.id)
    if (alertIndex === -1) {
      return getInitialWindow(state)
    }
    const possibleAlerts: IAlert[] = validAlerts.slice(alertIndex)
    return getInitialWindow(state, possibleAlerts)
}

export function validateWindow(state: IAlertState): IAlertWindow {
    const validWindowAlertIDs: string[] = state.window.fullAlerts.map(e => e.id);

    if (validWindowAlertIDs.length === 0) {
      return getInitialWindow(state);
    }
    const updatedWindows = [...state.window.windows];
    updatedWindows[state.window.currentWindowIndex] = state.window.windows[state.window.currentWindowIndex].filter((alertId) => validWindowAlertIDs.includes(alertId));

    // fullAlerts is rebuilt from items, so alerts that dropped out of items (dismissed,
    // culled, filter mismatch) are gone — window.alerts must not keep their ids or
    // currentAlertId/setWindowAlert can point at an alert that renders nothing.
    const fullAlerts: IAlert[] = state.alerts.items.filter((alert) => validWindowAlertIDs.includes(alert.id));
    const fullAlertIDs: string[] = fullAlerts.map((alert) => alert.id);
    const previousAlertId: string | undefined = state.window.currentAlertId;
    let currentAlertId: string | undefined = fullAlertIDs[0];
    if (previousAlertId != null && fullAlertIDs.includes(previousAlertId)) {
        currentAlertId = previousAlertId;
    }

    return {
        ...state.window,
        alerts: fullAlertIDs,
        fullAlerts: fullAlerts,
        windows: updatedWindows,
        currentAlert: Math.max(fullAlertIDs.indexOf(currentAlertId), 0),
        currentAlertId: currentAlertId,
        selected: currentAlertId != null ? [currentAlertId] : []
    }
}

export function buildWindowFromAlerts(alerts: IAlert[], alertFilter: IAlertFilter, windowSize: number, windowInterval: number): IAlert[] {
    if (alerts == null || alerts.length < 1) {
        return [];
    }

    const firstAlert: IAlert = alerts[0];
    const alertsMatchingFilter: IAlert[] = alerts.filter((alert) => alertMatchesCurrentFilter(alert, alertFilter));
    const alertsMatchingSite: IAlert[] = alertsMatchingFilter.filter((alert) => alert.site_id == firstAlert.site_id);

    const alertIndex = alertsMatchingSite.findIndex((alert) => alert.id === firstAlert.id);
    const currentWindow: IAlert[] = alertsMatchingSite.filter((alert: IAlert) => {
        const timeIntervalCheck = Math.abs(new Date(firstAlert.creation_timestamp).getTime() - new Date(alert.creation_timestamp).getTime());
        const alertWithinGroupInterval: boolean = windowInterval > 0 ? timeIntervalCheck < (windowInterval * 1e3) : true;
        return alertWithinGroupInterval == true;
    }).slice((alertIndex > -1 ? alertIndex : 1), windowSize);
    return currentWindow;
}

// TODO: build unit test
// TODO: Do not pass in state
export function moveAlertWindowNew(state: IAlertState, targetAlert: IAlert): IAlertWindow {
    const alertInCurrentWindow = state.window.alerts.includes(targetAlert.id);
    if (alertInCurrentWindow) {
        const alertIndex = state.window.alerts.indexOf(targetAlert.id);
        return {
            ...state.window,
            currentAlertId: targetAlert.id,
            currentAlert: alertIndex > 0 ? alertIndex : 0,
        }
    }

    const filter = state.filter;
    const windowSize = state.window.size;
    const windowInterval = state.window.interval;
    const validFilterAlerts: IAlert[] = getValidAlerts(state)
    const windows = buildAlertWindows(validFilterAlerts, filter, windowSize, windowInterval);
    const alertGroupIndex = findAlertGroupIndex(windows, targetAlert.id);

    if (alertGroupIndex === -1) {
        return getInitialWindow(state);
    }

    const fullAlerts: IAlert[] = state.alerts.items.filter((alert) => windows[alertGroupIndex].includes(alert.id));
    const fullAlertIDs: string[] = fullAlerts.map((alert) => alert.id);
    const firstAlertId: string | undefined = fullAlertIDs[0];
    if (firstAlertId == null) {
        return getInitialWindow(state);
    }

    return {
        ...state.window,
        currentAlert: 0,
        currentAlertId: firstAlertId,
        selected: [firstAlertId],
        alerts: fullAlertIDs,
        fullAlerts: fullAlerts
    }
}

export function nextAlertInWindow(alertWindow: string[], currentAlertIndex: number): { currentAlert: number, currentAlertId: string | undefined } {
    const newIndex = currentAlertIndex + 1;

    if(newIndex > (alertWindow.length - 1)) {
        const finalIndex = alertWindow.length - 1;

        return {
            currentAlert: finalIndex,
            currentAlertId: alertWindow[finalIndex]
        }
    }

    return {
        currentAlert: newIndex,
        currentAlertId: alertWindow[newIndex]
    }
}

export function listHash(list: string[]): string {
    return JSON.stringify(list)
}

export function buildAlertWindows(alerts: IAlert[], alertFilter: IAlertFilter, windowSize: number, windowInterval: number): string[][] {
    const foundWindows = new Set<string>();
    const filteredAlerts = [...alerts.filter((alert) => alertMatchesCurrentFilter(alert, alertFilter))];
    const windowedGroups: IAlert[][] = [];
    let currentGroupIndex = 0;
    let alertsCopy = [...filteredAlerts];

    filteredAlerts.forEach((alert) => {
        if (foundWindows.has(alert.id)) {
            return;
        }

        if (windowedGroups[currentGroupIndex] == null) {
            windowedGroups[currentGroupIndex] = [];
        }

        if (windowedGroups[currentGroupIndex].length >= windowSize) {
            currentGroupIndex = currentGroupIndex + 1;
            windowedGroups[currentGroupIndex] = [];
        }

        const currentWindow = buildWindowFromAlerts(alertsCopy, alertFilter, windowSize, windowInterval);
        if ((currentWindow != null && currentWindow.length > 0) && !foundWindows.has(alert.id)) {
            windowedGroups[currentGroupIndex] = [...currentWindow];
            const alertIds = windowedGroups[currentGroupIndex].map((_alert) => _alert.id);
            alertIds.forEach((id) => {
                foundWindows.add(id);
            });
        } else {
            windowedGroups[currentGroupIndex] = [alert];
            foundWindows.add(alert.id)
        }

        alertsCopy = [...alertsCopy.filter((_alert) => !foundWindows.has(_alert.id))];
        currentGroupIndex = currentGroupIndex + 1;
    });

    if(windowedGroups.length < 1) {
        return [];
    }

    return windowedGroups.map((group) => {
        return group.map((a: IAlert) => a.id)
    });
}

export function findAlertGroupIndex(groups: string[][], alertId: string): number {
    let alertGroupIndex = -1;

    for (let index = 0; index < groups.length; index++) {
        const alertIDs = groups[index];

        if (alertIDs.includes(alertId)) {
            alertGroupIndex = index;
            break;
        }
    }

    return alertGroupIndex;
}

export function findNextUnlockedWindowIndex(groups: string[][], lockedAlerts: Record<string, IAlertLock>, currentIndex: number, userId: string, dir = 1): number {
    let alertGroupIndex = -1;
    const alertLockMap = new Map<string, IAlertLock>(Object.entries(lockedAlerts));
    for (let index = currentIndex; (dir > 0 ? index < groups.length : index >= 0); index += dir) {
        const alertIDs = groups[index];
        let foundUnlocked = false;

        alertIDs.forEach((alertId) => {
            const userIdExists: boolean = userId != null;
            const lockMapContainsAlert: boolean = alertLockMap.get(alertId) != null;

            if (alertLockMap == null || (userIdExists && lockMapContainsAlert && alertLockMap.get(alertId)?.userID == userId)) {
                foundUnlocked = true;
            }
        });

        if (foundUnlocked) {
            alertGroupIndex = index;
            break;
        }
    }

    return alertGroupIndex;
}

export function buildUniqueWindow(currentGroups: string[][], alerts: IAlert[], alertFilter: IAlertFilter, windowSize: number, windowInterval: number): string[][] {
    const lockedGroups = new Set(currentGroups.map((window) => listHash(window)));
    const latestGroups = buildAlertWindows(alerts, alertFilter, windowSize, windowInterval)
    const newGroups = latestGroups.filter(list => {
        const key = listHash(list);
        if (lockedGroups.has(key)) {
          return false;
        }
        lockedGroups.add(key);
        return true;
    });

    return [...currentGroups, ...newGroups]
}
